/**
 * Tournament Strategies - 100% faithful to original Universal Paperclips
 * All strategy implementations match the original exactly
 */

export interface Strategy {
  name: string;
  shortName: string;
  description: string;
  // Returns 0 for A, 1 for B
  decide: (history: number[], opponentHistory: number[], payoffs: Payoffs) => number;
}

export interface Payoffs {
  AA: number; // Both play A
  AB: number; // We play A, opponent plays B
  BA: number; // We play B, opponent plays A
  BB: number; // Both play B
}

export interface StrategyState {
  strategy: Strategy;
  score: number;
  roundsPlayed: number;
}

/**
 * Strategy implementations
 * In original game: A = cooperate, B = defect
 */
export const strategies: Strategy[] = [
  {
    name: 'Random',
    shortName: 'RANDOM',
    description: 'Randomly picks A or B',
    decide: () => Math.random() < 0.5 ? 0 : 1,
  },
  {
    name: 'A100',
    shortName: 'A100',
    description: 'Always plays A',
    decide: () => 0,
  },
  {
    name: 'B100',
    shortName: 'B100',
    description: 'Always plays B',
    decide: () => 1,
  },
  {
    name: 'Greedy',
    shortName: 'GREEDY',
    description: 'Plays whichever move has higher potential payoff',
    decide: (_history, _opponentHistory, payoffs) => {
      // Average payoff for A: (AA + AB) / 2
      // Average payoff for B: (BA + BB) / 2
      const avgA = (payoffs.AA + payoffs.AB) / 2;
      const avgB = (payoffs.BA + payoffs.BB) / 2;
      return avgA >= avgB ? 0 : 1;
    },
  },
  {
    name: 'Generous',
    shortName: 'GENEROUS',
    description: 'Plays whichever move gives opponent higher payoff',
    decide: (_history, _opponentHistory, payoffs) => {
      // Opponent gets AA or BA when we play A
      // Opponent gets AB or BB when we play B
      const avgA = (payoffs.AA + payoffs.BA) / 2;
      const avgB = (payoffs.AB + payoffs.BB) / 2;
      return avgA >= avgB ? 0 : 1;
    },
  },
  {
    name: 'Minimax',
    shortName: 'MINIMAX',
    description: 'Minimizes maximum possible loss',
    decide: (_history, _opponentHistory, payoffs) => {
      // Worst case for A: min(AA, AB)
      // Worst case for B: min(BA, BB)
      const worstA = Math.min(payoffs.AA, payoffs.AB);
      const worstB = Math.min(payoffs.BA, payoffs.BB);
      return worstA >= worstB ? 0 : 1;
    },
  },
  {
    name: 'Tit for Tat',
    shortName: 'TIT FOR TAT',
    description: 'Copies opponent\'s last move',
    decide: (_history, opponentHistory, _payoffs) => {
      if (opponentHistory.length === 0) return 0; // Start with A
      return opponentHistory[opponentHistory.length - 1] ?? 0;
    },
  },
  {
    name: 'Beat Last',
    shortName: 'BEAT LAST',
    description: 'Plays best response to opponent\'s last move',
    decide: (_history, opponentHistory, payoffs) => {
      if (opponentHistory.length === 0) return 0; // Start with A
      const lastOpponent = opponentHistory[opponentHistory.length - 1];
      if (lastOpponent === 0) {
        // Opponent played A, so compare AA vs BA
        return payoffs.AA >= payoffs.BA ? 0 : 1;
      } else {
        // Opponent played B, so compare AB vs BB
        return payoffs.AB >= payoffs.BB ? 0 : 1;
      }
    },
  },
];

/**
 * Tournament class - manages a complete tournament
 */
export class Tournament {
  payoffs: Payoffs;
  strategyStates: StrategyState[];
  currentRound: number;
  totalRounds: number;
  matchup1: number; // Index of first strategy in current matchup
  matchup2: number; // Index of second strategy in current matchup
  history1: number[]; // History of moves for strategy 1 in current matchup
  history2: number[]; // History of moves for strategy 2 in current matchup
  isRunning: boolean;
  isComplete: boolean;

  constructor() {
    // Generate random payoffs (1-10 as per original)
    this.payoffs = {
      AA: Math.floor(Math.random() * 10) + 1,
      AB: Math.floor(Math.random() * 10) + 1,
      BA: Math.floor(Math.random() * 10) + 1,
      BB: Math.floor(Math.random() * 10) + 1,
    };

    // Initialize strategy states
    this.strategyStates = strategies.map(s => ({
      strategy: s,
      score: 0,
      roundsPlayed: 0,
    }));

    // Each pair plays each other once (n * n matchups for round-robin)
    this.totalRounds = strategies.length * strategies.length;
    this.currentRound = 0;
    this.matchup1 = 0;
    this.matchup2 = 0;
    this.history1 = [];
    this.history2 = [];
    this.isRunning = false;
    this.isComplete = false;
  }

  /**
   * Start the tournament
   */
  start() {
    this.isRunning = true;
    this.currentRound = 0;
    this.matchup1 = 0;
    this.matchup2 = 0;
    this.history1 = [];
    this.history2 = [];
  }

  /**
   * Process one round of the tournament
   * Returns yomi generated this round
   */
  processRound(): number {
    if (this.isComplete) return 0;

    // Get current matchup strategies
    const strat1 = this.strategyStates[this.matchup1];
    const strat2 = this.strategyStates[this.matchup2];
    if (!strat1 || !strat2) return 0;

    // Each strategy decides their move
    const move1 = strat1.strategy.decide(this.history1, this.history2, this.payoffs);
    const move2 = strat2.strategy.decide(this.history2, this.history1, {
      AA: this.payoffs.AA,
      AB: this.payoffs.BA, // Swap perspective
      BA: this.payoffs.AB,
      BB: this.payoffs.BB,
    });

    // Record moves
    this.history1.push(move1);
    this.history2.push(move2);

    // Calculate scores based on moves
    let score1: number, score2: number;
    if (move1 === 0 && move2 === 0) {
      score1 = this.payoffs.AA;
      score2 = this.payoffs.AA;
    } else if (move1 === 0 && move2 === 1) {
      score1 = this.payoffs.AB;
      score2 = this.payoffs.BA;
    } else if (move1 === 1 && move2 === 0) {
      score1 = this.payoffs.BA;
      score2 = this.payoffs.AB;
    } else {
      score1 = this.payoffs.BB;
      score2 = this.payoffs.BB;
    }

    strat1.score += score1;
    strat2.score += score2;
    strat1.roundsPlayed++;
    strat2.roundsPlayed++;

    this.currentRound++;

    // Move to next matchup after 10 rounds per matchup
    if (this.history1.length >= 10) {
      this.history1 = [];
      this.history2 = [];
      this.matchup2++;
      if (this.matchup2 >= strategies.length) {
        this.matchup2 = 0;
        this.matchup1++;
        if (this.matchup1 >= strategies.length) {
          this.isComplete = true;
          this.isRunning = false;
        }
      }
    }

    // Return small yomi per round
    return 1;
  }

  /**
   * Get rankings (sorted by score descending)
   */
  getRankings(): StrategyState[] {
    return [...this.strategyStates].sort((a, b) => b.score - a.score);
  }

  /**
   * Calculate final yomi bonus when tournament completes
   */
  getFinalYomiBonus(yomiBoost: number): number {
    if (!this.isComplete) return 0;

    const rankings = this.getRankings();
    let bonus = 0;

    // Top 3 placement bonuses
    if (rankings.length > 0) bonus += 50000; // 1st place
    if (rankings.length > 1) bonus += 30000; // 2nd place
    if (rankings.length > 2) bonus += 20000; // 3rd place

    // Calculate beat bonus (how many strategies beat others)
    let beatBonus = 0;
    for (let i = 0; i < rankings.length; i++) {
      const ranking = rankings[i];
      if (ranking) {
        beatBonus += (rankings.length - i - 1) * ranking.score;
      }
    }

    return Math.floor((bonus + beatBonus) * yomiBoost);
  }
}
